import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { jobService } from "./job.service";
import {
	type JobAssetAssociationParams,
	type JobAssignmentParams,
	type JobFileParams,
	type JobNoteParams,
	type JobParams,
	type JobRecordAssociationParams,
	type JobTypesListParams,
	type JobTypesQueryOptionsInput,
	normalizeJobTypesQueryOptions,
	type SubContractorParams,
} from "./job.types";

// ==================== QUERY KEYS ====================

export const jobKeys = {
	// Base keys
	all: ["job"] as const,

	// Jobs
	jobs: () => [...jobKeys.all, "jobs"] as const,
	jobsList: (start: number, count: number, filters?: JobParams) =>
		[...jobKeys.jobs(), "list", start, count, filters] as const,
	jobsDetail: (id: string, organizationId?: string) =>
		[...jobKeys.jobs(), "detail", id, organizationId] as const,
	jobsCount: (params?: JobParams) =>
		[...jobKeys.jobs(), "count", params] as const,
	jobsValidStatuses: (id: string) =>
		[...jobKeys.jobs(), id, "valid-statuses"] as const,
	jobTypesCount: (params?: JobTypesListParams) =>
		[...jobKeys.jobs(), "job-types", "count", params] as const,
	jobTypesPage: (start: number, count: number, params?: JobTypesListParams) =>
		[...jobKeys.jobs(), "job-types", "page", start, count, params] as const,
	jobTypesAll: (params?: JobTypesListParams) =>
		[...jobKeys.jobs(), "job-types", "all", params] as const,
	jobTypesForCreate: (organizationId?: string, subOrganizationId?: string) =>
		[
			...jobKeys.jobs(),
			"job-types",
			"for-create",
			organizationId ?? null,
			subOrganizationId ?? null,
		] as const,
	jobTypeDetail: (
		jobTypeId: string,
		subOrganizationId?: string,
		organizationId?: string,
	) =>
		[
			...jobKeys.jobs(),
			"job-type-detail",
			jobTypeId,
			subOrganizationId ?? "",
			organizationId ?? "",
		] as const,
	jobsFeatures: (start: number, count: number, filters?: JobParams) =>
		[...jobKeys.jobs(), "features", start, count, filters] as const,
	geometrySources: () => [...jobKeys.jobs(), "geometry-sources"] as const,
	gpsMethods: () => [...jobKeys.jobs(), "gps-methods"] as const,

	// Job Assignments
	jobAssignments: (jobId: string) =>
		[...jobKeys.jobs(), jobId, "assignments"] as const,
	jobAssignmentsCount: (jobId: string, params?: JobAssignmentParams) =>
		[...jobKeys.jobAssignments(jobId), "count", params] as const,
	jobAssignmentsList: (
		jobId: string,
		start: number,
		count: number,
		params?: JobAssignmentParams,
	) =>
		[...jobKeys.jobAssignments(jobId), "list", start, count, params] as const,

	// Job Sub-Contractors
	jobSubContractors: (jobId: string) =>
		[...jobKeys.jobs(), jobId, "sub-contractors"] as const,
	jobSubContractorsCount: (jobId: string, params?: SubContractorParams) =>
		[...jobKeys.jobSubContractors(jobId), "count", params] as const,
	jobSubContractorsList: (
		jobId: string,
		start: number,
		count: number,
		params?: SubContractorParams,
	) =>
		[
			...jobKeys.jobSubContractors(jobId),
			"list",
			start,
			count,
			params,
		] as const,

	// Job Type History
	jobTypeHistory: (jobTypeId: string) =>
		[...jobKeys.jobs(), "job-type-history", jobTypeId] as const,
	jobTypeHistoryCount: (jobTypeId: string) =>
		[...jobKeys.jobTypeHistory(jobTypeId), "count"] as const,
	jobTypeHistoryList: (jobTypeId: string, start: number, count: number) =>
		[...jobKeys.jobTypeHistory(jobTypeId), "list", start, count] as const,

	// Job History
	jobHistory: (jobId: string) => [...jobKeys.jobs(), jobId, "history"] as const,
	jobHistoryCount: (jobId: string) =>
		[...jobKeys.jobHistory(jobId), "count"] as const,
	jobHistoryList: (jobId: string, start: number, count: number) =>
		[...jobKeys.jobHistory(jobId), "list", start, count] as const,

	// Job ↔ asset associations
	jobAssets: (jobId: string) => [...jobKeys.jobs(), jobId, "assets"] as const,
	jobAssetsCount: (jobId: string, params?: JobAssetAssociationParams) =>
		[...jobKeys.jobAssets(jobId), "count", params] as const,
	jobAssetsList: (
		jobId: string,
		start: number,
		count: number,
		params?: JobAssetAssociationParams,
	) => [...jobKeys.jobAssets(jobId), "list", start, count, params] as const,

	// Job ↔ record associations
	jobRecords: (jobId: string) => [...jobKeys.jobs(), jobId, "records"] as const,
	jobRecordsCount: (jobId: string, params?: JobRecordAssociationParams) =>
		[...jobKeys.jobRecords(jobId), "count", params] as const,
	jobRecordsList: (
		jobId: string,
		start: number,
		count: number,
		params?: JobRecordAssociationParams,
	) => [...jobKeys.jobRecords(jobId), "list", start, count, params] as const,

	// Job Files (attachments)
	jobFiles: (jobId: string) => [...jobKeys.jobs(), jobId, "files"] as const,
	jobFilesCount: (jobId: string, params?: JobFileParams) =>
		[...jobKeys.jobFiles(jobId), "count", params] as const,
	jobFilesList: (
		jobId: string,
		start: number,
		count: number,
		params?: JobFileParams,
	) => [...jobKeys.jobFiles(jobId), "list", start, count, params] as const,
	jobFileContent: (jobId: string, fileId: string) =>
		[...jobKeys.jobFiles(jobId), "content", fileId] as const,
	jobFileThumbnail: (jobId: string, fileId: string) =>
		[...jobKeys.jobFiles(jobId), "thumbnail", fileId] as const,

	// Job Notes
	jobNotes: (jobId: string) => [...jobKeys.jobs(), jobId, "notes"] as const,
	jobNotesCount: (jobId: string, params?: JobNoteParams) =>
		[...jobKeys.jobNotes(jobId), "count", params] as const,
	jobNotesList: (
		jobId: string,
		start: number,
		count: number,
		params?: JobNoteParams,
	) => [...jobKeys.jobNotes(jobId), "list", start, count, params] as const,
} as const;

function jobTypesAllQueryOptions(options?: JobTypesQueryOptionsInput) {
	const params = normalizeJobTypesQueryOptions(options);
	return queryOptions({
		queryKey: jobKeys.jobTypesAll(params),
		queryFn: () => jobService.getJobTypes(options),
		staleTime: 10 * 60 * 1000,
		gcTime: 30 * 60 * 1000,
	});
}

/** Org-enabled job types only — Create Job modal / type dropdown (not org-admin tables). */
function jobTypesAvailableForCreateQueryOptions(
	organizationId: string,
	subOrganizationId?: string,
) {
	return queryOptions({
		queryKey: jobKeys.jobTypesForCreate(organizationId, subOrganizationId),
		queryFn: () =>
			jobService.getJobTypesForCreate(organizationId, subOrganizationId),
		staleTime: 0,
		gcTime: 5 * 60 * 1000,
	});
}

// ==================== JOB QUERY OPTIONS ====================

export const jobQueryOptions = {
	/**
	 * Get jobs with pagination and optional filtering
	 */
	list: (start: number = 1, count: number = 10, params?: JobParams) =>
		queryOptions({
			queryKey: jobKeys.jobsList(start, count, params),
			queryFn: () => jobService.getJobs(start, count, params),
			staleTime: 0,
			gcTime: 10 * 60 * 1000, // 10 minutes
		}),

	/**
	 * Get jobs count with optional filtering
	 */
	count: (params?: JobParams) =>
		queryOptions({
			queryKey: jobKeys.jobsCount(params),
			queryFn: ({ signal }) => jobService.getJobsCount(params, { signal }),
			staleTime: 0, // no stale time
			gcTime: 0, // 0
		}),

	/**
	 * Get a single job by ID
	 */
	detail: (id: string, options?: { organizationId?: string }) =>
		queryOptions({
			queryKey: jobKeys.jobsDetail(id, options?.organizationId),
			queryFn: () => jobService.getJob(id, options),
			staleTime: 5 * 60 * 1000, // 5 minutes
			gcTime: 10 * 60 * 1000, // 10 minutes
			enabled: !!id && id.length > 0,
		}),

	/**
	 * Get valid statuses for a job
	 */
	validStatuses: (id: string) =>
		queryOptions({
			queryKey: jobKeys.jobsValidStatuses(id),
			queryFn: () => jobService.getJobValidStatuses(id),
			staleTime: 10 * 60 * 1000, // 10 minutes
			gcTime: 30 * 60 * 1000, // 30 minutes
			enabled: !!id && id.length > 0,
		}),

	/**
	 * Geometry sources — GET /jobs/geometry-sources
	 */
	geometrySources: () =>
		queryOptions({
			queryKey: jobKeys.geometrySources(),
			queryFn: () => jobService.getGeometrySources(),
			staleTime: 30 * 60 * 1000,
			gcTime: 60 * 60 * 1000,
		}),

	/**
	 * GPS methods — GET /jobs/gps-methods
	 */
	gpsMethods: () =>
		queryOptions({
			queryKey: jobKeys.gpsMethods(),
			queryFn: () => jobService.getGpsMethods(),
			staleTime: 30 * 60 * 1000,
			gcTime: 60 * 60 * 1000,
		}),

	/**
	 * Job types count — GET /jobs/types/count
	 */
	typesCount: (options?: JobTypesQueryOptionsInput) => {
		const params = normalizeJobTypesQueryOptions(options);
		return queryOptions({
			queryKey: jobKeys.jobTypesCount(params),
			queryFn: () => jobService.getJobTypesCount(params),
			staleTime: 0,
			gcTime: 0,
		});
	},

	/**
	 * Job types page — GET /jobs/types/{start}/{count}
	 */
	typesPage: (
		start: number,
		count: number,
		options?: JobTypesQueryOptionsInput,
	) => {
		const params = normalizeJobTypesQueryOptions(options);
		return queryOptions({
			queryKey: jobKeys.jobTypesPage(start, count, params),
			queryFn: () => jobService.getJobTypes(options, { start, count }),
			staleTime: 0,
			gcTime: 0,
		});
	},

	/**
	 * All job types (paged client-side) — for search-over-full-list and dropdowns
	 */
	typesAll: (options?: JobTypesQueryOptionsInput) =>
		jobTypesAllQueryOptions(options),

	/**
	 * Get job types (for dropdowns, admin lists, etc.)
	 */
	types: (options?: JobTypesQueryOptionsInput) =>
		jobTypesAllQueryOptions(options),

	/**
	 * Org-enabled job types for Create Job (excludes org-disabled types).
	 */
	typesAvailableForCreate: (
		organizationId: string,
		subOrganizationId?: string,
	) =>
		jobTypesAvailableForCreateQueryOptions(organizationId, subOrganizationId),

	/**
	 * Get a single job type (detail / drawer)
	 */
	jobTypeDetail: (
		jobTypeId: string,
		options?: { subOrganizationId?: string; organizationId?: string },
	) =>
		queryOptions({
			queryKey: jobKeys.jobTypeDetail(
				jobTypeId,
				options?.subOrganizationId,
				options?.organizationId,
			),
			queryFn: () => jobService.getJobTypeById(jobTypeId, options),
			staleTime: 5 * 60 * 1000,
			gcTime: 15 * 60 * 1000,
			enabled: !!jobTypeId && jobTypeId.length > 0,
		}),

	/**
	 * Get jobs as GeoJSON features
	 */
	features: (start: number = 1, count: number = 10, params?: JobParams) =>
		queryOptions({
			queryKey: jobKeys.jobsFeatures(start, count, params),
			queryFn: () => jobService.getJobsFeatures(start, count, params),
			staleTime: 5 * 60 * 1000, // 5 minutes
			gcTime: 10 * 60 * 1000, // 10 minutes
		}),

	// ==================== JOB ASSIGNMENTS ====================

	/**
	 * Get job assignments count
	 */
	assignments: {
		count: (jobId: string, params?: JobAssignmentParams) =>
			queryOptions({
				queryKey: jobKeys.jobAssignmentsCount(jobId, params),
				queryFn: () => jobService.getJobAssignmentsCount(jobId, params),
				staleTime: 5 * 60 * 1000, // 5 minutes
				gcTime: 10 * 60 * 1000, // 10 minutes
				enabled: !!jobId && jobId.length > 0,
			}),

		/**
		 * Get job assignments with pagination
		 */
		list: (
			jobId: string,
			start: number = 1,
			count: number = 10,
			params?: JobAssignmentParams,
		) =>
			queryOptions({
				queryKey: jobKeys.jobAssignmentsList(jobId, start, count, params),
				queryFn: () =>
					jobService.getJobAssignments(jobId, start, count, params),
				staleTime: 5 * 60 * 1000, // 5 minutes
				gcTime: 10 * 60 * 1000, // 10 minutes
				enabled: !!jobId && jobId.length > 0,
			}),

		/**
		 * Infinite query for job assignments
		 * @param totalCount - Optional total count for more accurate pagination
		 */
		infinite: (
			jobId: string,
			params?: JobAssignmentParams,
			pageSize: number = 10,
			totalCount?: number,
		) =>
			infiniteQueryOptions({
				queryKey: [
					...jobKeys.jobAssignmentsList(
						jobId,
						1, // API uses 1-based pagination
						pageSize,
						params,
					),
					"infinite",
				],
				queryFn: ({ pageParam }) =>
					jobService.getJobAssignments(
						jobId,
						pageParam, // No default needed - initialPageParam handles it
						pageSize,
						params,
					),
				initialPageParam: 1, // API requires 1-based pagination
				getNextPageParam: (lastPage, allPages, lastPageParam) => {
					// If we have a total count, check against it for accuracy
					if (totalCount !== undefined) {
						const fetched = allPages.reduce(
							(acc, page) => acc + page.length,
							0,
						);
						if (fetched >= totalCount) {
							return undefined;
						}
					}

					// If the last page has fewer items than pageSize, we've reached the end
					if (lastPage.length < pageSize) {
						return undefined;
					}

					// Calculate next page start index (1-based pagination)
					return lastPageParam + pageSize;
				},
				getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
					if (firstPageParam <= 1) {
						return undefined;
					}
					return Math.max(1, firstPageParam - pageSize);
				},
				staleTime: 5 * 60 * 1000, // 5 minutes
				gcTime: 10 * 60 * 1000, // 10 minutes
				enabled: !!jobId && jobId.length > 0,
			}),
	},

	// ==================== JOB SUB-CONTRACTORS ====================

	/**
	 * Get job sub-contractors count
	 */
	subContractors: {
		count: (jobId: string, params?: SubContractorParams) =>
			queryOptions({
				queryKey: jobKeys.jobSubContractorsCount(jobId, params),
				queryFn: () => jobService.getJobSubContractorsCount(jobId, params),
				staleTime: 5 * 60 * 1000, // 5 minutes
				gcTime: 10 * 60 * 1000, // 10 minutes
				enabled: !!jobId && jobId.length > 0,
			}),

		/**
		 * Get job sub-contractors with pagination
		 */
		list: (
			jobId: string,
			start: number = 1,
			count: number = 10,
			params?: SubContractorParams,
		) =>
			queryOptions({
				queryKey: jobKeys.jobSubContractorsList(jobId, start, count, params),
				queryFn: () =>
					jobService.getJobSubContractors(jobId, start, count, params),
				staleTime: 5 * 60 * 1000, // 5 minutes
				gcTime: 10 * 60 * 1000, // 10 minutes
				enabled: !!jobId && jobId.length > 0,
			}),

		/**
		 * Infinite query for job sub-contractors
		 * @param totalCount - Optional total count for more accurate pagination
		 */
		infinite: (
			jobId: string,
			params?: SubContractorParams,
			pageSize: number = 10,
			totalCount?: number,
		) =>
			infiniteQueryOptions({
				queryKey: [
					...jobKeys.jobSubContractorsList(
						jobId,
						1, // API uses 1-based pagination
						pageSize,
						params,
					),
					"infinite",
				],
				queryFn: ({ pageParam }) =>
					jobService.getJobSubContractors(
						jobId,
						pageParam, // No default needed - initialPageParam handles it
						pageSize,
						params,
					),
				initialPageParam: 1, // API requires 1-based pagination
				getNextPageParam: (lastPage, allPages, lastPageParam) => {
					// If we have a total count, check against it for accuracy
					if (totalCount !== undefined) {
						const fetched = allPages.reduce(
							(acc, page) => acc + page.length,
							0,
						);
						if (fetched >= totalCount) {
							return undefined;
						}
					}

					// If the last page has fewer items than pageSize, we've reached the end
					if (lastPage.length < pageSize) {
						return undefined;
					}

					// Calculate next page start index (1-based pagination)
					return lastPageParam + pageSize;
				},
				getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
					if (firstPageParam <= 1) {
						return undefined;
					}
					return Math.max(1, firstPageParam - pageSize);
				},
				staleTime: 5 * 60 * 1000, // 5 minutes
				gcTime: 10 * 60 * 1000, // 10 minutes
				enabled: !!jobId && jobId.length > 0,
			}),
	},

	// ==================== JOB TYPE HISTORY ====================

	jobTypeHistory: {
		count: (jobTypeId: string, organizationId?: string) =>
			queryOptions({
				queryKey: [...jobKeys.jobTypeHistoryCount(jobTypeId), organizationId],
				queryFn: () =>
					jobService.getJobTypeHistoryCount(jobTypeId, organizationId),
				staleTime: 5 * 60 * 1000,
				gcTime: 10 * 60 * 1000,
				enabled: !!jobTypeId && jobTypeId.length > 0,
			}),

		infinite: (
			jobTypeId: string,
			pageSize: number = 10,
			totalCount: number,
			organizationId?: string,
		) =>
			infiniteQueryOptions({
				queryKey: [
					...jobKeys.jobTypeHistoryList(jobTypeId, 1, pageSize),
					"infinite",
					totalCount,
					organizationId,
				],
				queryFn: ({ pageParam }) =>
					jobService.getJobTypeHistory(
						jobTypeId,
						pageParam,
						pageSize,
						organizationId,
					),
				initialPageParam: totalCount
					? Math.max(1, totalCount - pageSize + 1)
					: 1,
				getNextPageParam: (_lastPage, _allPages, lastPageParam) => {
					if (lastPageParam <= 1) return undefined;
					return Math.max(1, lastPageParam - pageSize);
				},
				getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
					if (!totalCount || firstPageParam >= totalCount - pageSize + 1) {
						return undefined;
					}
					return firstPageParam + pageSize;
				},
				staleTime: 5 * 60 * 1000,
				gcTime: 10 * 60 * 1000,
				enabled:
					!!jobTypeId && jobTypeId.length > 0 && totalCount !== undefined,
			}),
	},

	// ==================== JOB HISTORY ====================

	/**
	 * Job history query options
	 */
	history: {
		/**
		 * Get job history count
		 */
		count: (jobId: string) =>
			queryOptions({
				queryKey: jobKeys.jobHistoryCount(jobId),
				queryFn: () => jobService.getJobHistoryCount(jobId),
				staleTime: 5 * 60 * 1000, // 5 minutes
				gcTime: 10 * 60 * 1000, // 10 minutes
				enabled: !!jobId && jobId.length > 0,
			}),

		/**
		 * Get job history with pagination
		 */
		list: (jobId: string, start: number = 1, count: number = 10) =>
			queryOptions({
				queryKey: jobKeys.jobHistoryList(jobId, start, count),
				queryFn: () => jobService.getJobHistory(jobId, start, count),
				staleTime: 5 * 60 * 1000, // 5 minutes
				gcTime: 10 * 60 * 1000, // 10 minutes
				enabled: !!jobId && jobId.length > 0,
			}),

		/**
		 * Infinite query for job history (newest first)
		 * @param totalCount - Required for reverse pagination
		 */
		infinite: (jobId: string, pageSize: number = 10, totalCount: number) =>
			infiniteQueryOptions({
				queryKey: [
					...jobKeys.jobHistoryList(jobId, 1, pageSize),
					"infinite",
					totalCount,
				],
				queryFn: ({ pageParam }) =>
					jobService.getJobHistory(jobId, pageParam, pageSize),
				// Start from the end (most recent items)
				initialPageParam: totalCount
					? Math.max(1, totalCount - pageSize + 1)
					: 1,
				getNextPageParam: (_lastPage, _allPages, lastPageParam) => {
					// If we've fetched everything or reached the beginning, stop
					if (lastPageParam <= 1) {
						return undefined;
					}

					// Calculate next page start (going backwards)
					const nextStart = lastPageParam - pageSize;

					// If next batch would start before 1, clamp to 1 to get remaining items
					return Math.max(1, nextStart);
				},
				getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
					// For reverse pagination, "previous" would be newer items
					// This isn't typically needed but keeping for completeness
					if (!totalCount || firstPageParam >= totalCount - pageSize + 1) {
						return undefined;
					}
					return firstPageParam + pageSize;
				},
				staleTime: 5 * 60 * 1000,
				gcTime: 10 * 60 * 1000,
				// Only enable when we have both jobId and totalCount
				enabled: !!jobId && jobId.length > 0 && totalCount !== undefined,
			}),
	},

	// ==================== JOB ASSET ASSOCIATIONS ====================

	/**
	 * Job ↔ asset association query options
	 */
	assets: {
		count: (
			jobId: string,
			params?: JobAssetAssociationParams,
			options?: { organizationId?: string },
		) =>
			queryOptions({
				queryKey: jobKeys.jobAssetsCount(jobId, params),
				queryFn: () => jobService.getJobAssetsCount(jobId, params, options),
				staleTime: 30 * 1000,
				gcTime: 5 * 60 * 1000,
				enabled: !!jobId && jobId.length > 0,
			}),

		list: (
			jobId: string,
			start: number = 1,
			count: number = 50,
			params?: JobAssetAssociationParams,
			options?: { organizationId?: string },
		) =>
			queryOptions({
				queryKey: jobKeys.jobAssetsList(jobId, start, count, params),
				queryFn: () =>
					jobService.getJobAssets(jobId, start, count, params, options),
				staleTime: 30 * 1000,
				gcTime: 5 * 60 * 1000,
				enabled: !!jobId && jobId.length > 0,
			}),
	},

	// ==================== JOB RECORD ASSOCIATIONS ====================

	/**
	 * Job ↔ record association query options
	 */
	records: {
		count: (
			jobId: string,
			params?: JobRecordAssociationParams,
			options?: { organizationId?: string },
		) =>
			queryOptions({
				queryKey: jobKeys.jobRecordsCount(jobId, params),
				queryFn: () => jobService.getJobRecordsCount(jobId, params, options),
				staleTime: 30 * 1000,
				gcTime: 5 * 60 * 1000,
				enabled: !!jobId && jobId.length > 0,
			}),

		list: (
			jobId: string,
			start: number = 1,
			count: number = 50,
			params?: JobRecordAssociationParams,
			options?: { organizationId?: string },
		) =>
			queryOptions({
				queryKey: jobKeys.jobRecordsList(jobId, start, count, params),
				queryFn: () =>
					jobService.getJobRecords(jobId, start, count, params, options),
				staleTime: 30 * 1000,
				gcTime: 5 * 60 * 1000,
				enabled: !!jobId && jobId.length > 0,
			}),
	},

	// ==================== JOB FILES ====================

	/**
	 * Job attachment (file) query options
	 */
	files: {
		/**
		 * Get job files count
		 */
		count: (
			jobId: string,
			params?: JobFileParams,
			options?: { organizationId?: string },
		) =>
			queryOptions({
				queryKey: jobKeys.jobFilesCount(jobId, params),
				queryFn: () => jobService.getJobFilesCount(jobId, params, options),
				staleTime: 30 * 1000,
				gcTime: 5 * 60 * 1000,
				enabled: !!jobId && jobId.length > 0,
			}),

		/**
		 * Get job files with pagination
		 */
		list: (
			jobId: string,
			start: number = 1,
			count: number = 50,
			params?: JobFileParams,
			options?: { organizationId?: string },
		) =>
			queryOptions({
				queryKey: jobKeys.jobFilesList(jobId, start, count, params),
				queryFn: () =>
					jobService.getJobFiles(jobId, start, count, params, options),
				staleTime: 30 * 1000,
				gcTime: 5 * 60 * 1000,
				enabled: !!jobId && jobId.length > 0,
			}),

		/**
		 * Get a job file content blob for preview / download.
		 */
		content: (
			jobId: string,
			fileId: string,
			options?: { organizationId?: string },
		) =>
			queryOptions({
				queryKey: jobKeys.jobFileContent(jobId, fileId),
				queryFn: () => jobService.getJobFile(jobId, fileId, options),
				staleTime: 5 * 60 * 1000,
				gcTime: 30 * 60 * 1000,
				enabled: !!jobId && !!fileId,
			}),

		/**
		 * Get a job file thumbnail blob (null when missing)
		 */
		thumbnail: (
			jobId: string,
			fileId: string,
			options?: { organizationId?: string },
		) =>
			queryOptions({
				queryKey: jobKeys.jobFileThumbnail(jobId, fileId),
				queryFn: async () => {
					try {
						return await jobService.getJobFileThumbnail(jobId, fileId, options);
					} catch {
						return null;
					}
				},
				staleTime: 5 * 60 * 1000,
				gcTime: 30 * 60 * 1000,
				enabled: !!jobId && !!fileId,
				retry: false,
			}),
	},

	// ==================== JOB NOTES ====================

	/**
	 * Job notes query options
	 */
	notes: {
		/**
		 * Get job notes count
		 */
		count: (
			jobId: string,
			params?: JobNoteParams,
			options?: { organizationId?: string },
		) =>
			queryOptions({
				queryKey: jobKeys.jobNotesCount(jobId, params),
				queryFn: () => jobService.getJobNotesCount(jobId, params, options),
				staleTime: 30 * 1000,
				gcTime: 5 * 60 * 1000,
				enabled: !!jobId && jobId.length > 0,
			}),

		/**
		 * Get job notes with pagination
		 */
		list: (
			jobId: string,
			start: number = 1,
			count: number = 50,
			params?: JobNoteParams,
			options?: { organizationId?: string },
		) =>
			queryOptions({
				queryKey: jobKeys.jobNotesList(jobId, start, count, params),
				queryFn: () =>
					jobService.getJobNotes(jobId, start, count, params, options),
				staleTime: 30 * 1000,
				gcTime: 5 * 60 * 1000,
				enabled: !!jobId && jobId.length > 0,
			}),
	},
};

// ==================== CONVENIENCE EXPORTS ====================

/**
 * All job query options grouped by category
 */
export const jobQueries = {
	// Core job queries
	jobs: jobQueryOptions,

	// Nested resource queries
	assignments: jobQueryOptions.assignments,
	subContractors: jobQueryOptions.subContractors,
	history: jobQueryOptions.history,
	assets: jobQueryOptions.assets,
	records: jobQueryOptions.records,
	files: jobQueryOptions.files,
	notes: jobQueryOptions.notes,
} as const;

/**
 * Query key factory for easy invalidation
 */
export const jobQueryKeys = jobKeys;
